import { WeatherData } from '../types/weather';

// Cabuyao, Laguna, Philippines coordinates
const CABUYAO_LOCATION = {
  latitude: 14.2786,
  longitude: 121.1242,
};

export interface WeatherApiStatus {
  connected: boolean;
  error: string | null;
  lastFetch: number | null;
}

class WeatherApiService {
  private onStatusChangeCallbacks: ((status: WeatherApiStatus) => void)[] = [];
  private onDataCallbacks: ((data: WeatherData) => void)[] = [];
  private status: WeatherApiStatus = {
    connected: false,
    error: null,
    lastFetch: null,
  };
  private pollingInterval: NodeJS.Timeout | null = null;
  private isPolling: boolean = false;
  private readonly POLL_INTERVAL_MS = 30000; // 30 seconds

  /**
   * Pick the nearest hourly index from Open-Meteo API data
   */
  private pickNearestIndex(times: string[]): number {
    if (!times || !times.length) return 0;
    const now = Date.now();
    let best = 0;
    let bestDiff = Math.abs(now - Date.parse(times[0]));
    for (let i = 1; i < times.length; i++) {
      const diff = Math.abs(now - Date.parse(times[i]));
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return best;
  }

  /**
   * Fetch current weather data from Open-Meteo API for Cabuyao, Laguna
   */
  async fetchWeather(): Promise<WeatherData> {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${CABUYAO_LOCATION.latitude}&longitude=${CABUYAO_LOCATION.longitude}&current_weather=true&hourly=temperature_2m,relative_humidity_2m,pressure_msl,wind_speed_10m,wind_direction_10m,precipitation_probability`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Open-Meteo API HTTP ${response.status}`);
      }

      const data = await response.json();
      const cw = data.current_weather || {};
      const hr = data.hourly || {};
      const idx = this.pickNearestIndex(hr.time || []);

      // Map API data to our WeatherData format
      const reading: WeatherData = {
        temperature: cw.temperature ?? hr.temperature_2m?.[idx] ?? null,
        humidity: hr.relative_humidity_2m?.[idx] ?? null,
        pressure: hr.pressure_msl?.[idx] ? hr.pressure_msl[idx] / 100 : null, // Convert Pa to hPa
        windSpeed: cw.windspeed ?? hr.wind_speed_10m?.[idx] ?? null,
        windDirection: cw.winddirection ?? hr.wind_direction_10m?.[idx] ?? null,
        light: null, // Not available from Open-Meteo
        rain: hr.precipitation_probability?.[idx] ?? null, // Use precipitation probability as rain percentage
        fan: false, // Default to off, will be managed separately
        timestamp: Date.now(),
      };

      // Update status
      this.status = {
        connected: true,
        error: null,
        lastFetch: Date.now(),
      };
      this.notifyStatusChange(this.status);

      // Notify all data callbacks
      this.onDataCallbacks.forEach((callback) => callback(reading));

      return reading;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch weather data';
      this.status = {
        connected: false,
        error: errorMessage,
        lastFetch: this.status.lastFetch,
      };
      this.notifyStatusChange(this.status);
      throw error;
    }
  }

  /**
   * Start polling the API at regular intervals
   */
  startPolling(intervalMs: number = this.POLL_INTERVAL_MS) {
    if (this.isPolling) {
      console.log('⏳ API polling already active');
      return;
    }

    this.isPolling = true;
    // Fetch immediately
    this.fetchWeather().catch((err) => {
      console.error('❌ Initial API fetch failed:', err);
    });

    // Then poll at intervals
    this.pollingInterval = setInterval(() => {
      this.fetchWeather().catch((err) => {
        console.error('❌ API poll failed:', err);
      });
    }, intervalMs);

    console.log(`✅ Started API polling (${intervalMs}ms interval)`);
  }

  /**
   * Stop polling the API
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    console.log('🛑 Stopped API polling');
  }

  /**
   * Subscribe to weather data updates
   */
  subscribe(callback: (data: WeatherData) => void): () => void {
    this.onDataCallbacks.push(callback);
    return () => {
      const index = this.onDataCallbacks.indexOf(callback);
      if (index > -1) {
        this.onDataCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(callback: (status: WeatherApiStatus) => void): () => void {
    this.onStatusChangeCallbacks.push(callback);
    // Immediately call with current status
    callback(this.status);
    return () => {
      const index = this.onStatusChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.onStatusChangeCallbacks.splice(index, 1);
      }
    };
  }

  private notifyStatusChange(status: WeatherApiStatus) {
    this.onStatusChangeCallbacks.forEach((callback) => callback(status));
  }

  /**
   * Get current status
   */
  getStatus(): WeatherApiStatus {
    return { ...this.status };
  }

  /**
   * Check if currently polling
   */
  isPollingActive(): boolean {
    return this.isPolling;
  }
}

export const weatherApiService = new WeatherApiService();

